import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  RefreshCw,
  Search,
  AlertCircle,
  FileText,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Calendar,
  DollarSign,
  Clock,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ArrowRight,
  Check
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar as CalendarPicker } from "../ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";

const STATUS_COLORS = {
  Scheduled: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  Invoiced: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300",
  InvoiceRequested: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300",
  PartiallyPaid: "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300",
  Paid: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300",
  Overdue: "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300",
  Waived: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
};

function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
}

// Money still owed vs money already banked — the Paid tab has no "amount due"
// to show (the backend zeroes it), so it reports what actually came in.
const amountDueOf = (m) => m.amount_due || 0;
const amountPaidOf = (m) => m.paid_amount || m.calculated_amount || 0;

// Which way a column sorts on its FIRST click. Money reads biggest-first;
// names and dates read the way you'd scan them.
const SORT_FIRST_DIR = {
  program: "asc",
  milestone: "asc",
  due_date: "asc",
  amount: "desc",
  status: "asc"
};

// Only these roles pass the backend's is_finance_user gate on the milestone
// PATCH — PMs can see this list but their edits would 403.
const FINANCE_ROLES = ["COO", "Admin", "Accountant"];

// Stored due dates may be date-only ("2026-08-04") or full ISO datetimes.
// Build the Date from parts so the picker doesn't drift a day across timezones.
function parseDueDate(value) {
  if (!value) return undefined;
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

function toDateOnly(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const formatDayLabel = (date) =>
  date.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });

/**
 * Click-to-edit due date. `emptyLabel`/`alwaysShowIcon` let the Needs Date
 * dialog present the same control as an explicit "Set date" action, where a
 * dash with a hover-only icon would hide the one thing you opened it to do.
 * onSaved receives the saved date so callers can update in place instead of
 * refetching.
 */
function EditableDueDate({
  milestone,
  canEdit,
  onSaved,
  emptyLabel = "—",
  alwaysShowIcon = false
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const selected = parseDueDate(milestone.due_date);

  const label = (
    <span className={`text-sm tabular-nums ${milestone.is_overdue ? "text-red-600 font-medium" : ""}`}>
      {milestone.due_date_formatted || emptyLabel}
    </span>
  );

  if (!canEdit) return label;

  const handleSelect = async (date) => {
    if (!date) return;
    const dateOnly = toDateOnly(date);
    if (milestone.due_date && milestone.due_date.slice(0, 10) === dateOnly) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      // ManualDate pins the picked day so a later rule recalc can't overwrite
      // it; the project page's schedule tab reads the same document.
      await apiClient.patch(
        `/programs/${milestone.program_id}/payments/milestones/${milestone.id}`,
        { due_date: dateOnly, due_rule_type: "ManualDate" }
      );
      setOpen(false);
      toast.success(`"${milestone.title}" due date moved to ${formatDayLabel(date)}`);
      onSaved?.(dateOnly);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update due date");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={saving}
          title="Change due date"
          data-testid={`milestone-due-date-${milestone.id}`}
          className="group/due inline-flex items-center gap-1.5 rounded-md px-2 py-1 -my-1 hover:bg-muted transition-colors"
        >
          {label}
          {saving ? (
            <RefreshCw className="w-3 h-3 animate-spin text-muted-foreground" />
          ) : (
            <Calendar className={`w-3 h-3 text-muted-foreground transition-opacity ${open || alwaysShowIcon ? "opacity-100" : "opacity-0 group-hover/due:opacity-100"}`} />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="center">
        <CalendarPicker
          mode="single"
          selected={selected}
          defaultMonth={selected || new Date()}
          onSelect={handleSelect}
          disabled={saving}
        />
      </PopoverContent>
    </Popover>
  );
}

// A milestone whose money is settled: a due date on it changes no forecast, so
// these drop to the bottom of the Needs Date list.
const SETTLED_STATUSES = ["Paid", "Waived"];
const isSettled = (m) => SETTLED_STATUSES.includes(m.status);

// Why a milestone has no date — the rule that was meant to produce one. Stated
// as what the rule does, not as a diagnosis, since a blank date can mean either
// the trigger hasn't fired or the source date it points at is itself missing.
const DUE_RULE_HINTS = {
  OnMilestoneCompletion: "Set by a delivery trigger",
  OnDeliverableCount: "Set by a deliverable count",
  OnStartDate: "Follows the project start date",
  RelativeDaysFromStart: "Counts from the project start date",
  OnActivation: "Follows the activation date"
};
const dueRuleHint = (m) => DUE_RULE_HINTS[m.due_rule_type] || "No date entered";

function NeedsDateRow({ milestone, canEdit, savedDate, onProgramClick, onSaved }) {
  const settled = isSettled(milestone);
  const amount = settled ? amountPaidOf(milestone) : amountDueOf(milestone);

  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 border-b border-border last:border-0 hover:bg-muted/40 transition-colors"
      data-testid={`needs-date-row-${milestone.id}`}
    >
      <div className="min-w-0 flex-1 basis-[200px]">
        <button
          type="button"
          onClick={() => onProgramClick(milestone.program_id)}
          className="text-left text-sm font-medium hover:text-primary transition-colors truncate max-w-full block"
          title="Open the project's payment schedule"
        >
          {milestone.program_name}
        </button>
        <span className="text-xs text-muted-foreground truncate block">
          {milestone.title} · {milestone.pm_name}
        </span>
      </div>

      <div className="hidden sm:block min-w-0 flex-1 basis-[130px] text-xs text-muted-foreground truncate">
        {dueRuleHint(milestone)}
      </div>

      <div className="text-right basis-[80px] flex-shrink-0">
        <div className="text-sm font-semibold tabular-nums">{formatCurrency(amount)}</div>
        <div className="text-[11px] text-muted-foreground">{settled ? "collected" : "due"}</div>
      </div>

      <Badge className={`text-xs flex-shrink-0 ${STATUS_COLORS[milestone.status] || STATUS_COLORS.Scheduled}`}>
        {milestone.status}
      </Badge>

      {/* ml-auto keeps the date control on the right even on the narrow widths
          where the row wraps it onto a second line. */}
      <div className="flex-shrink-0 ml-auto sm:basis-[150px] text-right">
        {savedDate ? (
          // Stays in the list once dated so you can see what you just set (and
          // change it) instead of the row vanishing under the cursor.
          <span
            className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-emerald-700 dark:text-emerald-400"
            data-testid={`needs-date-saved-${milestone.id}`}
          >
            <Check className="w-3.5 h-3.5 flex-shrink-0" />
            <EditableDueDate
              milestone={{ ...milestone, due_date: savedDate, due_date_formatted: formatDayLabel(parseDueDate(savedDate)) }}
              canEdit={canEdit}
              onSaved={onSaved}
              alwaysShowIcon
            />
          </span>
        ) : canEdit ? (
          <span data-testid={`needs-date-set-${milestone.id}`}>
            <EditableDueDate
              milestone={milestone}
              canEdit
              onSaved={onSaved}
              emptyLabel="Set date"
              alwaysShowIcon
            />
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </div>
    </div>
  );
}

/**
 * The Needs Date banner's popup: every undated payment milestone, with a date
 * picker on each row. Rows are snapshotted when the dialog opens so dating one
 * doesn't reshuffle the list mid-pass; the overview refetches once on close.
 */
function NeedsDateDialog({ open, onOpenChange, milestones, canEdit, onProgramClick, onClosed }) {
  const [rows, setRows] = useState([]);
  const [savedDates, setSavedDates] = useState({});
  const [showSettled, setShowSettled] = useState(false);

  // Seed once per open: a refresh landing mid-pass must not reorder the list
  // under the cursor or wipe the marks on what was just dated.
  const seededRef = useRef(false);
  useEffect(() => {
    if (!open) {
      seededRef.current = false;
      return;
    }
    if (seededRef.current) return;
    seededRef.current = true;
    setRows(milestones || []);
    setSavedDates({});
    setShowSettled(false);
  }, [open, milestones]);

  const { pending, settled } = useMemo(() => ({
    pending: rows.filter((m) => !isSettled(m)),
    settled: rows.filter(isSettled)
  }), [rows]);

  const savedCount = Object.keys(savedDates).length;
  const remaining = pending.filter((m) => !savedDates[m.id]).length;

  const handleSaved = (milestone) => (dateOnly) => {
    setSavedDates((prev) => ({ ...prev, [milestone.id]: dateOnly }));
  };

  const handleOpenChange = (next) => {
    onOpenChange(next);
    // Only pay for a refetch of the whole outlook if something actually moved.
    if (!next && savedCount > 0) onClosed?.();
  };

  const rowProps = (m) => ({
    milestone: m,
    canEdit,
    savedDate: savedDates[m.id],
    onProgramClick: (programId) => {
      handleOpenChange(false);
      onProgramClick(programId);
    },
    onSaved: handleSaved(m)
  });

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-4xl w-[calc(100vw-2rem)] p-0 gap-0 max-h-[85vh] flex flex-col"
        data-testid="needs-date-dialog"
      >
        <DialogHeader className="p-5 pb-4 pr-12 space-y-1.5 text-left flex-shrink-0">
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-sans)' }}>
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            Milestones missing due dates
          </DialogTitle>
          <DialogDescription>
            Undated milestones sit outside the monthly outlook and can never go overdue.
            {canEdit
              ? " Pick a date on any row to pull it into the forecast — it saves right away."
              : " Setting dates needs COO, Admin or Accountant access."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 px-5 pb-3 text-sm flex-shrink-0 flex-wrap">
          <span className="font-medium" data-testid="needs-date-remaining">
            {remaining} still {remaining === 1 ? "needs" : "need"} a date
          </span>
          {savedCount > 0 && (
            <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300 text-xs">
              {savedCount} dated
            </Badge>
          )}
          {settled.length > 0 && (
            <span className="text-muted-foreground text-xs">
              · {settled.length} already settled
            </span>
          )}
        </div>

        <div className="overflow-y-auto flex-1 border-t border-border">
          {pending.length === 0 && settled.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              Every payment milestone has a due date.
            </div>
          ) : (
            <>
              {pending.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  Nothing unpaid is missing a date.
                </div>
              ) : (
                pending.map((m) => <NeedsDateRow key={m.id} {...rowProps(m)} />)
              )}

              {settled.length > 0 && (
                <div className="border-t border-border">
                  <button
                    type="button"
                    onClick={() => setShowSettled((v) => !v)}
                    className="w-full flex items-center justify-between gap-2 px-4 py-3 text-sm text-muted-foreground hover:bg-muted/40 transition-colors"
                    data-testid="needs-date-settled-toggle"
                  >
                    <span>
                      {settled.length} already paid or waived — no date needed
                    </span>
                    {showSettled ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {showSettled && settled.map((m) => <NeedsDateRow key={m.id} {...rowProps(m)} />)}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 p-4 border-t border-border flex-shrink-0 flex-wrap">
          <p className="text-xs text-muted-foreground max-w-md">
            A date set here is pinned manually, so a delivery trigger won't move it afterwards.
          </p>
          <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)}>
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SortableHeader({ columnKey, label, sort, onSort, className = "" }) {
  const active = sort?.key === columnKey;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;

  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onSort(columnKey)}
        title={`Sort by ${label}`}
        data-testid={`milestones-sort-${columnKey}`}
        className={`group inline-flex items-center gap-1 select-none hover:text-foreground transition-colors ${active ? "text-foreground" : ""}`}
      >
        <span>{label}</span>
        <Icon
          className={`w-3.5 h-3.5 flex-shrink-0 transition-opacity ${
            active ? "opacity-100" : "opacity-0 group-hover:opacity-50"
          }`}
        />
      </button>
    </TableHead>
  );
}

/**
 * Milestone rows with click-to-sort headers. Sorting is local to each block, so
 * one month can be sorted by amount while another stays in date order; a third
 * click on the same column drops back to the server's ordering (overdue first).
 */
function MilestoneTable({
  milestones,
  onProgramClick,
  canEditDates = false,
  onDueDateSaved,
  dueLabel = "Due Date",
  amountLabel = "Amount Due",
  amountOf = amountDueOf,
  headerClassName = "bg-muted/30",
  rowClassName,
  testIdPrefix = "milestone"
}) {
  const [sort, setSort] = useState(null);

  const handleSort = (key) => {
    setSort((prev) => {
      const firstDir = SORT_FIRST_DIR[key] || "asc";
      if (prev?.key !== key) return { key, dir: firstDir };
      if (prev.dir === firstDir) return { key, dir: firstDir === "asc" ? "desc" : "asc" };
      return null;
    });
  };

  const sorted = useMemo(() => {
    if (!sort) return milestones;
    const accessors = {
      program: (m) => (m.program_name || "").toLowerCase(),
      milestone: (m) => (m.title || "").toLowerCase(),
      due_date: (m) => m.due_date || "9999",
      amount: (m) => amountOf(m),
      status: (m) => (m.status || "").toLowerCase()
    };
    const get = accessors[sort.key];
    const factor = sort.dir === "asc" ? 1 : -1;
    return [...milestones].sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      if (av < bv) return -factor;
      if (av > bv) return factor;
      return 0;
    });
  }, [milestones, sort, amountOf]);

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className={headerClassName}>
            <SortableHeader columnKey="program" label="Program" sort={sort} onSort={handleSort} className="w-[200px]" />
            <SortableHeader columnKey="milestone" label="Milestone" sort={sort} onSort={handleSort} />
            <SortableHeader columnKey="due_date" label={dueLabel} sort={sort} onSort={handleSort} className="text-center" />
            <SortableHeader columnKey="amount" label={amountLabel} sort={sort} onSort={handleSort} className="text-right" />
            <SortableHeader columnKey="status" label="Status" sort={sort} onSort={handleSort} className="text-center" />
            <TableHead className="text-center w-[80px]">Invoice</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((m) => (
            <TableRow key={m.id} className={rowClassName ? rowClassName(m) : ""}>
              <TableCell>
                <button
                  onClick={() => onProgramClick(m.program_id)}
                  className="text-left hover:text-primary transition-colors font-medium text-sm truncate max-w-[180px] block"
                  data-testid={`${testIdPrefix}-program-${m.id}`}
                >
                  {m.program_name}
                </button>
                <span className="text-xs text-muted-foreground truncate block">
                  {m.pm_name}
                </span>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{m.title}</span>
                  {m.notes && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-[200px] text-xs">{m.notes}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">{m.type}</span>
              </TableCell>
              <TableCell className="text-center">
                <EditableDueDate milestone={m} canEdit={canEditDates} onSaved={onDueDateSaved} />
              </TableCell>
              <TableCell className="text-right">
                <span className="font-semibold text-sm tabular-nums">
                  {formatCurrency(amountOf(m))}
                </span>
                {m.status === "PartiallyPaid" && (
                  <span className="text-xs text-muted-foreground block">
                    of {formatCurrency(m.calculated_amount)}
                  </span>
                )}
              </TableCell>
              <TableCell className="text-center">
                <Badge className={`text-xs ${STATUS_COLORS[m.status] || STATUS_COLORS.Scheduled}`}>
                  {m.status}
                </Badge>
              </TableCell>
              <TableCell className="text-center">
                {m.invoice_url ? (
                  <a
                    href={m.invoice_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-muted transition-colors"
                  >
                    <ExternalLink className="w-4 h-4 text-blue-600" />
                  </a>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function OverdueBlock({ overdue, totalDue, onProgramClick, canEditDates, onDueDateSaved }) {
  const [expanded, setExpanded] = useState(true);
  if (!overdue || overdue.length === 0) return null;

  return (
    <Card className="border-red-300 dark:border-red-800/60 bg-red-50/40 dark:bg-red-950/10" data-testid="overdue-block">
      <CardHeader
        className="py-3 px-4 cursor-pointer hover:bg-red-100/40 dark:hover:bg-red-950/20 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <CardTitle className="text-lg font-semibold text-red-700 dark:text-red-300 truncate" style={{ fontFamily: 'var(--font-sans)' }}>
              Past-Due From Previous Months
            </CardTitle>
            <Badge variant="destructive" className="text-xs flex-shrink-0">
              {overdue.length} unpaid
            </Badge>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="hidden sm:flex items-center gap-4 text-sm">
              <span className="text-red-700/80 dark:text-red-300/80">
                {overdue.length} milestone{overdue.length !== 1 ? 's' : ''}
              </span>
              <span className="font-semibold text-red-700 dark:text-red-300 tabular-nums">
                {formatCurrency(totalDue)}
              </span>
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        <div className="flex sm:hidden items-center gap-4 mt-2 text-sm">
          <span className="text-red-700/80 dark:text-red-300/80">{overdue.length} milestone{overdue.length !== 1 ? 's' : ''}</span>
          <span className="font-semibold text-red-700 dark:text-red-300 tabular-nums">{formatCurrency(totalDue)}</span>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="p-0">
          <MilestoneTable
            milestones={overdue}
            onProgramClick={onProgramClick}
            canEditDates={canEditDates}
            onDueDateSaved={onDueDateSaved}
            dueLabel="Original Due Date"
            headerClassName="bg-red-100/30 dark:bg-red-900/20"
            rowClassName={() => "bg-red-50/30 dark:bg-red-900/5"}
            testIdPrefix="overdue"
          />
        </CardContent>
      )}
    </Card>
  );
}

// The three states a month's money can be in. `null` — the default view — is
// everything still owed (overdue + scheduled); Paid lives on its own tab so
// settled rows stop padding the list you actually have to work.
const MONTH_TABS = [
  {
    key: "overdue",
    label: "Overdue",
    active: "bg-red-100 text-red-700 ring-red-300 dark:bg-red-900/50 dark:text-red-300 dark:ring-red-800",
    empty: "Nothing overdue this month"
  },
  {
    key: "scheduled",
    label: "Scheduled",
    active: "bg-slate-200 text-slate-700 ring-slate-300 dark:bg-slate-700 dark:text-slate-200 dark:ring-slate-600",
    empty: "Nothing scheduled this month"
  },
  {
    key: "paid",
    label: "Paid",
    active: "bg-emerald-100 text-emerald-700 ring-emerald-300 dark:bg-emerald-900/50 dark:text-emerald-300 dark:ring-emerald-800",
    empty: "No payments collected this month"
  }
];

function MonthBlock({ month, onProgramClick, canEditDates, onDueDateSaved }) {
  const [expanded, setExpanded] = useState(true);
  const [tab, setTab] = useState(null);

  const groups = useMemo(() => {
    const g = { overdue: [], scheduled: [], paid: [] };
    (month.milestones || []).forEach((m) => {
      if (m.status === "Paid") g.paid.push(m);
      else if (m.is_overdue) g.overdue.push(m);
      else g.scheduled.push(m);
    });
    return g;
  }, [month.milestones]);

  const paidView = tab === "paid";
  const amountOf = paidView ? amountPaidOf : amountDueOf;
  const visible = tab ? groups[tab] : [...groups.overdue, ...groups.scheduled];
  const visibleTotal = visible.reduce((sum, m) => sum + amountOf(m), 0);
  const hasContent = (month.milestones || []).length > 0;

  const selectTab = (e, key) => {
    e.stopPropagation();
    setTab((prev) => (prev === key ? null : key));
    setExpanded(true);
  };

  return (
    <Card className={`${!hasContent ? 'opacity-60' : ''}`} data-testid={`month-block-${month.month_key}`}>
      <CardHeader
        className="py-3 px-4 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Calendar className="w-5 h-5 text-muted-foreground flex-shrink-0" />
            <CardTitle className="text-lg font-semibold truncate" style={{ fontFamily: 'var(--font-sans)' }}>
              {month.month_label}
            </CardTitle>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
            {MONTH_TABS.map((t) => {
              const count = groups[t.key].length;
              // With no tab picked we're showing everything still owed, so both
              // unpaid pills read as selected.
              const isActive = tab === t.key || (tab === null && t.key !== "paid");
              return (
                <button
                  key={t.key}
                  type="button"
                  disabled={count === 0}
                  onClick={(e) => selectTab(e, t.key)}
                  title={count === 0 ? `No ${t.label.toLowerCase()} milestones` : `Show ${t.label.toLowerCase()} milestones`}
                  data-testid={`month-tab-${month.month_key}-${t.key}`}
                  className={`
                    inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs transition-colors ring-1
                    ${count === 0
                      ? 'bg-transparent text-muted-foreground/50 ring-border cursor-default'
                      : isActive
                        ? `${t.active} cursor-pointer`
                        : 'bg-transparent text-muted-foreground ring-border hover:bg-muted cursor-pointer'}
                  `}
                >
                  <span className="font-semibold tabular-nums">{count}</span>
                  <span>{t.label}</span>
                </button>
              );
            })}
            <div className="flex items-center gap-2 ml-auto sm:ml-0">
              <span
                className="text-sm font-semibold tabular-nums"
                title={paidView ? "Collected this month" : "Still due"}
              >
                {formatCurrency(visibleTotal)}
              </span>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="p-0">
          {visible.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground text-sm">
              {tab
                ? MONTH_TABS.find((t) => t.key === tab)?.empty
                : "No milestones due this month"}
            </div>
          ) : (
            <MilestoneTable
              milestones={visible}
              onProgramClick={onProgramClick}
              canEditDates={canEditDates}
              onDueDateSaved={onDueDateSaved}
              amountLabel={paidView ? "Amount Paid" : "Amount Due"}
              amountOf={amountOf}
              rowClassName={(m) => (m.is_overdue ? "bg-red-50/50 dark:bg-red-900/10" : "")}
            />
          )}
        </CardContent>
      )}
    </Card>
  );
}

export default function MilestonesOverview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEditDates = FINANCE_ROLES.includes(user?.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [pmFilter, setPmFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [pms, setPms] = useState([]);
  const [searchDebounce, setSearchDebounce] = useState("");
  const [needsDateOpen, setNeedsDateOpen] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounce(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status_filter", statusFilter);
      if (pmFilter !== "all") params.append("pm_filter", pmFilter);
      if (searchDebounce) params.append("search", searchDebounce);
      
      const res = await apiClient.get(`/milestones-overview?${params.toString()}`);
      setData(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Access denied - Admin/COO/Accountant only");
      } else {
        toast.error("Failed to load milestones overview");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter, pmFilter, searchDebounce]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    apiClient.get("/users").then(res => {
      const pmList = res.data.filter(u => 
        ["COO", "Program Manager", "Marketing PM", "Sales"].includes(u.role)
      );
      setPms(pmList);
    }).catch(() => {});
  }, []);

  const handleProgramClick = (programId) => {
    navigate(`/programs/${programId}?tab=milestone-payments`);
  };

  // The banner and the KPI card count only what a due date would change: an
  // undated milestone that's already paid is bookkeeping, not work. Counted off
  // the same list and predicate the dialog uses, so the three always agree.
  const needsDate = useMemo(() => {
    const all = data?.needs_date || [];
    const settled = all.filter(isSettled).length;
    return { unpaid: all.length - settled, settled, total: all.length };
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="milestones-overview" className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg sm:text-xl font-light tracking-tight flex items-center gap-2" style={{ fontFamily: 'var(--font-sans)' }}>
            <DollarSign className="w-5 h-5" />
            Milestones Overview
          </h2>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
            3-month payment milestone outlook
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="h-8 w-fit"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          <span className="ml-2 hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* Summary Cards */}
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="p-3">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <DollarSign className="w-3.5 h-3.5" />
              Total Due
            </div>
            <div className="text-lg sm:text-xl font-light">{formatCurrency(data.grand_total_due)}</div>
          </Card>
          <Card className="p-3">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <Clock className="w-3.5 h-3.5" />
              Milestones
            </div>
            <div className="text-lg sm:text-xl font-light">{data.grand_total_count}</div>
          </Card>
          <Card className="p-3 h-full" data-testid="overdue-summary-card">
            <div className="flex items-center gap-2 text-red-600 text-xs mb-1">
              <AlertTriangle className="w-3.5 h-3.5" />
              Overdue
            </div>
            <div className="text-lg sm:text-xl font-light text-red-600 tabular-nums">{data.grand_overdue_count}</div>
            {/* Must be grand_overdue_due, not overdue_total_due: the latter is
                the prior-month section's subtotal, so pairing it with the grand
                count showed 4 overdue above the money for one of them. */}
            {data.grand_overdue_due > 0 && (
              <div className="text-xs text-red-600/80 tabular-nums mt-0.5">{formatCurrency(data.grand_overdue_due)}</div>
            )}
          </Card>
          {/* Stays clickable even at zero unpaid, so the list is still reachable
              once the banner above has gone away. */}
          <button
            type="button"
            onClick={() => setNeedsDateOpen(true)}
            disabled={needsDate.total === 0}
            title={needsDate.total === 0 ? "Every milestone has a due date" : "Review milestones missing due dates"}
            data-testid="needs-date-card"
            className="text-left"
          >
            <Card className={`p-3 h-full transition-colors ${needsDate.total > 0 ? "cursor-pointer hover:border-amber-300 dark:hover:border-amber-700" : ""}`}>
              <div className="flex items-center gap-2 text-amber-600 text-xs mb-1">
                <AlertCircle className="w-3.5 h-3.5" />
                Needs Date
              </div>
              <div className="text-lg sm:text-xl font-light text-amber-600 tabular-nums">{needsDate.unpaid}</div>
              {needsDate.settled > 0 && (
                <div className="text-xs text-muted-foreground tabular-nums mt-0.5">
                  +{needsDate.settled} already paid
                </div>
              )}
            </Card>
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search programs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
            data-testid="milestones-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px] h-9" data-testid="milestones-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="unpaid">Unpaid</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="invoiced">Invoiced</SelectItem>
          </SelectContent>
        </Select>
        <Select value={pmFilter} onValueChange={setPmFilter}>
          <SelectTrigger className="w-[160px] h-9" data-testid="milestones-pm-filter">
            <SelectValue placeholder="PM" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All PMs</SelectItem>
            {pms.map(pm => (
              <SelectItem key={pm.id} value={pm.id}>{pm.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Needs Date Banner — opens the list so the dates can be filled in.
          Counts unpaid only: nothing to chase means no banner. */}
      {data && needsDate.unpaid > 0 && (
        <button
          type="button"
          onClick={() => setNeedsDateOpen(true)}
          data-testid="needs-date-banner"
          title="Review these milestones and set their due dates"
          className="w-full text-left bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 flex items-center gap-3 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
        >
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <span className="text-sm text-amber-800 dark:text-amber-200">
            <strong>{needsDate.unpaid}</strong> unpaid payment milestone{needsDate.unpaid !== 1 ? 's' : ''} missing due date{needsDate.unpaid !== 1 ? 's' : ''}
          </span>
          <span className="ml-auto flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-300 flex-shrink-0">
            <span className="hidden sm:inline">{canEditDates ? "Set dates" : "Review"}</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </button>
      )}

      <NeedsDateDialog
        open={needsDateOpen}
        onOpenChange={setNeedsDateOpen}
        milestones={data?.needs_date}
        canEdit={canEditDates}
        onProgramClick={handleProgramClick}
        onClosed={() => fetchData(true)}
      />

      {/* Overdue Section — always rendered above current/next/following months */}
      {data && data.overdue && data.overdue.length > 0 && (
        <OverdueBlock
          overdue={data.overdue}
          totalDue={data.overdue_total_due}
          onProgramClick={handleProgramClick}
          canEditDates={canEditDates}
          onDueDateSaved={() => fetchData(true)}
        />
      )}

      {/* Month Blocks */}
      {data && (
        <div className="space-y-4">
          {data.months.map((month) => (
            <MonthBlock
              key={month.month_key}
              month={month}
              onProgramClick={handleProgramClick}
              canEditDates={canEditDates}
              onDueDateSaved={() => fetchData(true)}
            />
          ))}
        </div>
      )}

    </div>
  );
}
